import { type DataProvider } from 'react-admin';
import jsonServerProvider from 'ra-data-json-server';
import axiosInstance from './axiosConfig';
import { getBackendLanguage } from './i18n';
import * as Sentry from '@sentry/react';
import { toOrderHistoryQuery } from '../pages/orderHistory/orderHistoryTypes';

const axiosHttpClient = async (url: string, options: any = {}) => {
  const { method = 'GET', body } = options;

  try {
    const response = await axiosInstance({
      url,
      method,
      data: body,
    });

    const headers = new Headers();

    Object.keys(response.headers).forEach((key) => {
      headers.set(key, response.headers[key]);
    });

    if (response.headers['x-total-count']) {
      headers.set('X-Total-Count', response.headers['x-total-count']);
    }

    return {
      status: response.status,
      headers: headers,
      body: JSON.stringify(response.data),
      json: response.data,
    };
  } catch (error: any) {
    Sentry.captureException(error);
    return Promise.reject({
      status: error.response?.status || 500,
      message: error.response?.data?.message || error.message,
      body: error.response?.data,
    });
  }
};

const apiUrl = process.env.REACT_APP_API_URL!;
const base = jsonServerProvider(apiUrl, axiosHttpClient);

const map: Record<string, Partial<Record<keyof DataProvider, string>>> = {
  members: {
    getList: 'admin/member/list',
    getOne: 'admin/members',
    getMany: 'admin/members',
    create: 'admin/member/add',
    update: 'admin/members',
    delete: 'admin/members',
  },
  locationsManagement: {
    getList: 'location/list',
  },
  riderManagement: {
    getList: 'admin/courier/management/list',
  },
  commissionCouriers: {
    getList: 'admin/courier/management/list',
  },
  locationItems: {
    getList: '/restaurant/get/admin',
  },
  riderPerformance: {
    getList: 'admin/courier/management/list',
  },
  itemsManagement: {
    getList: 'menu-item/get/list',
  },
  categories: {
    getList: 'category/list',
  },
  coupons: {
    getList: 'coupon/list',
    getOne: 'coupon',
    create: 'coupon/create',
    update: 'coupon',
    delete: 'coupon',
  },
  discounts: {
    getList: 'discount/list',
    getOne: 'discount',
    create: 'discount/create',
    update: 'discount',
    delete: 'discount',
  },
  orderHistory: {
    getList: 'order/history',
    getOne: 'order/history',
  },
  variantTemplates: {
    getList: 'variant-template/list',
    getOne: 'variant-template',
    create: 'variant-template/create',
    update: 'variant-template',
    delete: 'variant-template',
  },
};

// Formularz grupy wariantow trzyma size/price jako tekst (wola na nich .trim()),
// a backend szablonow zwraca i przyjmuje liczby. Konwersja jak przy zapisie
// wariantow produktu: nieliczbowy albo pusty size idzie jako null.
const toTemplateRecord = (record: any) =>
  record?.items
    ? {
        ...record,
        items: record.items.map((i: any) => ({
          name: i.name ?? '',
          size: i.size != null ? String(i.size) : '',
          price: i.price != null ? String(i.price) : '',
        })),
      }
    : record;

const toTemplatePayload = (data: any) =>
  data?.items
    ? {
        ...data,
        items: data.items.map((i: any) => {
          const size = String(i.size ?? '').trim();
          return {
            name: i.name,
            size: size !== '' && !Number.isNaN(Number(size)) ? Number(size) : null,
            price: Number(i.price),
          };
        }),
      }
    : data;

const apiDataProvider: DataProvider = {
  ...base,
  getList: (resource, params) => {
    // Historia zamowien: dni z filtrow -> zakres chwil [createdFrom, createdTo) dla BE.
    const filter = resource === 'orderHistory' ? toOrderHistoryQuery(params.filter) : params.filter;
    const newFilter = { ...filter, language: getBackendLanguage() };

    // The items endpoint returns `{ restaurantImageUrl, menuItems: [...] }`
    // instead of a bare array, so we unwrap it here and expose the
    // restaurant image through `meta` for the list header.
    if (resource === 'itemsManagement') {
      return axiosInstance
        .get(map[resource]!.getList!, { params: newFilter })
        .then((response) => {
          const body = response.data;
          const items = Array.isArray(body) ? body : (body?.menuItems ?? []);

          // The backend endpoint has no pagination support and always returns
          // every item for the restaurant, so we slice to the requested page
          // here to make the Datagrid's pagination footer actually work.
          const { page, perPage } = params.pagination ?? { page: 1, perPage: items.length };
          const start = (page - 1) * perPage;
          const pageItems = items.slice(start, start + perPage);

          return {
            data: pageItems,
            total: items.length,
            meta: { restaurantImageUrl: Array.isArray(body) ? undefined : body?.restaurantImageUrl },
          };
        });
    }

    return base
      .getList(map[resource]?.getList || resource, {
        ...params,
        filter: newFilter,
      })
      .then((result) => {
        // Kategoria domyslna ("Inne" / "Others") powstaje automatycznie razem
        // z lokalizacja i sluzy jako worek na produkty bez przypisania — nie ma
        // po co jej kasowac ani zmieniac, wiec nie pokazujemy jej w ekranie
        // zarzadzania kategoriami.
        //
        // Filtrujemy tutaj, a nie na backendzie, bo ten sam endpoint
        // `/category/list` karmi rowniez select kategorii w formularzu produktu
        // (locationsCreateItemPage wola go bezposrednio przez axios, poza
        // dataProviderem) — tam "Inne" musi zostac wybieralne.
        const data =
          resource === 'categories'
            ? result.data.filter((record: any) => !record.isDefault)
            : result.data;

        return {
          ...result,
          data,
          // Licznik musi isc za odfiltrowana lista, inaczej paginacja pokazuje
          // "1-2 z 2" przy jednym widocznym wierszu.
          total: data.length === result.data.length ? (result.total ?? data.length) : data.length,
        };
      });
  },
  getOne: (resource, params) => base.getOne(map[resource]?.getOne || resource, params),
  getMany: (resource, params) => base.getMany(map[resource]?.getMany || resource, params),
  create: (resource, params) => base.create(map[resource]?.create || resource, params),
  update: (resource, params) => base.update(map[resource]?.update || resource, params),
  delete: (resource, params) => base.delete(map[resource]?.delete || resource, params),
  getManyReference: (resource, params) =>
    base.getManyReference(map[resource]?.getManyReference || resource, params),
};

// Normalizacja rekordow szablonow wariantow (patrz toTemplateRecord/toTemplatePayload).
export const dataProvider: DataProvider = {
  ...apiDataProvider,
  getList: (resource, params) =>
    resource === 'variantTemplates'
      ? apiDataProvider
          .getList(resource, params)
          .then((result) => ({ ...result, data: result.data.map(toTemplateRecord) }))
      : apiDataProvider.getList(resource, params),
  getOne: (resource, params) =>
    resource === 'variantTemplates'
      ? apiDataProvider.getOne(resource, params).then((result) => ({ ...result, data: toTemplateRecord(result.data) }))
      : apiDataProvider.getOne(resource, params),
  create: (resource, params) =>
    resource === 'variantTemplates'
      ? apiDataProvider
          .create(resource, { ...params, data: toTemplatePayload(params.data) })
          .then((result) => ({ ...result, data: toTemplateRecord(result.data) }))
      : apiDataProvider.create(resource, params),
  update: (resource, params) =>
    resource === 'variantTemplates'
      ? apiDataProvider
          .update(resource, { ...params, data: toTemplatePayload(params.data) })
          .then((result) => ({ ...result, data: toTemplateRecord(result.data) }))
      : apiDataProvider.update(resource, params),
};

export default dataProvider;
